// src/components/TransactionForm.js
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, writeBatch, doc, addDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { addMonths, format, parseISO } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { inferCategory } from '../utils/categories';
import {
    Box, TextField, Button, Select, MenuItem, FormControl, InputLabel,
    Grid, Typography, Divider, Autocomplete
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Repeat as RepeatIcon,
    CreditCard as CreditCardIcon,
    Close as CloseIcon,
} from '@mui/icons-material';

const TYPE_OPTIONS = [
    { value: 'income', label: 'Entrada', icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, color: '#10b981' },
    { value: 'fixedExpense', label: 'Despesa Fixa (recorrente)', icon: <RepeatIcon sx={{ fontSize: 16 }} />, color: '#f59e0b' },
    { value: 'variableExpense', label: 'Despesa Variável', icon: <TrendingDownIcon sx={{ fontSize: 16 }} />, color: '#f43f5e' },
    { value: 'installment', label: 'Parcelamento', icon: <CreditCardIcon sx={{ fontSize: 16 }} />, color: '#6366f1' },
];

const TransactionForm = ({ onTransactionAdded, onClose, transactionToEdit, categoryOptions = [], defaultDate }) => {
    const isEditing = Boolean(transactionToEdit);
    const initialTransaction = useMemo(() => transactionToEdit || {}, [transactionToEdit]);

    const [description, setDescription] = useState(initialTransaction.description || '');
    const [amount, setAmount] = useState(initialTransaction.amount?.toString() || '');
    const [date, setDate] = useState(
        initialTransaction.date
            ? format(initialTransaction.date, 'yyyy-MM-dd')
            : format(defaultDate || new Date(), 'yyyy-MM-dd')
    );
    const [type, setType] = useState(initialTransaction.type || 'variableExpense');
    const [category, setCategory] = useState(initialTransaction.category || inferCategory(initialTransaction));
    const [categoryTouched, setCategoryTouched] = useState(Boolean(initialTransaction.category));
    const [installments, setInstallments] = useState(1);
    const [cardName, setCardName] = useState(initialTransaction.cardName || '');
    const [repeatMonths, setRepeatMonths] = useState(12);
    const [loading, setLoading] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        if (!categoryTouched) {
            setCategory(inferCategory({ description, type, cardName }));
        }
    }, [cardName, categoryTouched, description, type]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const transactionData = {
            description: description.trim(),
            amount: parseFloat(amount),
            date: parseISO(date),
            type,
            installments: parseInt(installments),
            cardName: cardName.trim(),
            repeatMonths: parseInt(repeatMonths),
            category: category.trim(),
        };

        try {
            if (isEditing) {
                const updatePayload = {
                    description: transactionData.description,
                    amount: transactionData.amount,
                    date: transactionData.date,
                    type: transactionData.type,
                    category: transactionData.category || inferCategory(transactionData),
                    cardName: transactionData.type === 'installment' ? transactionData.cardName : deleteField(),
                };

                if (transactionData.type !== 'installment') {
                    updatePayload.isInstallment = deleteField();
                    updatePayload.installmentDetails = deleteField();
                }

                if (transactionData.type !== 'fixedExpense') {
                    updatePayload.isRecurring = deleteField();
                    updatePayload.recurringId = deleteField();
                }

                await updateDoc(doc(db, 'transactions', transactionToEdit.id), updatePayload);
            } else if ((transactionData.type === 'installment' && transactionData.installments > 1) || transactionData.type === 'fixedExpense') {
                const batch = writeBatch(db);
                const isInstallment = transactionData.type === 'installment';
                const loopCount = isInstallment ? transactionData.installments : transactionData.repeatMonths;
                const uniqueId = uuidv4();

                for (let i = 0; i < loopCount; i++) {
                    const transactionDate = addMonths(transactionData.date, i);
                    const docRef = doc(collection(db, 'transactions'));
                    let newDoc = {
                        userId: currentUser.uid,
                        amount: isInstallment ? transactionData.amount / loopCount : transactionData.amount,
                        type: transactionData.type,
                        date: transactionDate,
                        description: isInstallment
                            ? `${transactionData.description} (${i + 1}/${loopCount})`
                            : transactionData.description,
                        category: transactionData.category || inferCategory(transactionData),
                    };
                    if (isInstallment) {
                        newDoc.isInstallment = true;
                        newDoc.installmentDetails = { installmentId: uniqueId, current: i + 1, total: loopCount };
                        newDoc.cardName = transactionData.cardName;
                    } else {
                        newDoc.isRecurring = true;
                        newDoc.recurringId = uniqueId;
                    }
                    batch.set(docRef, newDoc);
                }
                await batch.commit();
            } else {
                await addDoc(collection(db, 'transactions'), {
                    userId: currentUser.uid,
                    description: transactionData.description,
                    amount: transactionData.amount,
                    type: transactionData.type,
                    date: transactionData.date,
                    category: transactionData.category || inferCategory(transactionData),
                });
            }
            onTransactionAdded();
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const selectedType = TYPE_OPTIONS.find(o => o.value === type);

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                p: 3,
                borderRadius: '16px',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                <Typography variant="h6" fontWeight={700}>
                    {isEditing ? 'Editar Lançamento' : 'Novo Lançamento'}
                </Typography>
                <Button onClick={onClose} size="small" sx={{ minWidth: 0, p: 0.5, color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                </Button>
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Descrição"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        fullWidth required
                        placeholder="Ex: Supermercado, Salário..."
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Valor (R$)"
                        type="number"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        fullWidth required
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Data"
                        type="date"
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        fullWidth required
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Autocomplete
                        freeSolo
                        options={categoryOptions}
                        value={category}
                        onChange={(_, newValue) => {
                            setCategory(newValue || '');
                            setCategoryTouched(true);
                        }}
                        onInputChange={(_, newValue) => {
                            setCategory(newValue);
                            setCategoryTouched(true);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Categoria"
                                placeholder="Ex: Alimentação, Moradia..."
                                helperText="Sugestões criadas a partir dos lançamentos já cadastrados"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select value={type} label="Tipo" onChange={e => setType(e.target.value)}>
                            {TYPE_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ color: opt.color, display: 'flex' }}>{opt.icon}</Box>
                                        {opt.label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {type === 'fixedExpense' && !isEditing && (
                    <Grid item xs={12}>
                        <TextField
                            label="Repetir por quantos meses?"
                            type="number"
                            value={repeatMonths}
                            onChange={e => setRepeatMonths(e.target.value)}
                            inputProps={{ min: 1 }}
                            fullWidth
                            helperText="A despesa será lançada automaticamente nos próximos meses"
                        />
                    </Grid>
                )}

                {type === 'installment' && (
                    <>
                        {!isEditing && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nº de Parcelas"
                                    type="number"
                                    value={installments}
                                    onChange={e => setInstallments(e.target.value)}
                                    inputProps={{ min: 2 }}
                                    fullWidth
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome do Cartão"
                                value={cardName}
                                onChange={e => setCardName(e.target.value)}
                                fullWidth
                                placeholder="Ex: Nubank, Itaú..."
                            />
                        </Grid>
                    </>
                )}

                {/* Preview */}
                {amount && (
                    <Grid item xs={12}>
                        <Box sx={{
                            p: 1.5, borderRadius: '8px',
                            backgroundColor: `${selectedType?.color}15`,
                            border: `1px solid ${selectedType?.color}30`,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <Typography variant="body2" color="text.secondary">
                                {type === 'installment' && installments > 1
                                    ? `${installments}x de R$ ${(parseFloat(amount || 0) / parseInt(installments || 1)).toFixed(2)}`
                                    : 'Valor total'}
                            </Typography>
                            <Typography fontWeight={700} sx={{ color: selectedType?.color }}>
                                R$ {parseFloat(amount || 0).toFixed(2)}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 0.5 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit" sx={{ color: 'text.secondary', borderColor: 'divider' }}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{ minWidth: 120 }}
                    >
                        {loading ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TransactionForm;
